import React, { useEffect } from 'react';

import {
  QAlert,
  QBox,
  QFlex,
  QFormControl,
  QHeading,
  QIcon,
  QMultiSelect,
  QStack,
  QText,
  QTag,
  QDivider,
  QLink,
} from '@qualio/ui-components';
import { FormikProps } from 'formik';
import { useCompanyUsersQuery, useConfigsQuery, usePoliciesQuery, usePolicyLinksQuery } from 'hooks';
import { getRequiredAudits } from 'lib';
import { Document, File } from 'providers';
import { useTranslation } from 'react-i18next';
import { routes } from 'utils';
import { ViewFileCard } from 'v2components';
import { AddSupplierFormikPayload, ConfigItem } from 'v2types';

import WarningMessage from '../WarningMessage/WarningMessage';

type ApprovalFormProps = {
  formik: FormikProps<AddSupplierFormikPayload>;
  handleStepChange: (stepIndex: number) => void;
};

const ADDITIONAL_DOCUMENTS = 'Additional documents';
const ApprovalSimpleItem: React.FC<{ title: string; description?: string }> = ({ title, description }) => (
  <span>
    <QText fontSize="sm" weight="semibold">
      {title}
    </QText>
    <QText color="gray.500" fontSize="sm" whiteSpace="pre-wrap">
      {description ? description : '--'}
    </QText>
  </span>
);

export const ApprovalForm: React.FC<ApprovalFormProps> = ({ formik, handleStepChange }) => {
  const { t } = useTranslation();
  const { values, errors, touched, setFieldValue, status } = formik;
  const { usersOptions, isCompanyUsersLoading } = useCompanyUsersQuery();

  const isFieldInvalid = (field: keyof AddSupplierFormikPayload) => {
    return !!touched && !!touched[field] && !!errors[field];
  };

  const formDocuments = values.documents;
  const { policyDocuments } = usePolicyLinksQuery(formDocuments, values.supplierType?.value, values.riskType?.id);
  const { isConfigsLoading, auditTypeOptions } = useConfigsQuery();
  const { policyLinks } = usePoliciesQuery(isConfigsLoading);
  const requiredAudits = getRequiredAudits(
    policyLinks,
    auditTypeOptions as unknown as ConfigItem[],
    values.supplierType as unknown as ConfigItem,
    values.riskType as unknown as ConfigItem,
  );

  useEffect(() => {
    if (formDocuments.length === 0) {
      setFieldValue('documents', policyDocuments);
    }
  }, [policyDocuments, setFieldValue, formDocuments.length]);

  const showWarningAlert = status === 'touched' && !!touched && Object.keys(errors).length > 0;

  useEffect(() => {
    if (showWarningAlert) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [showWarningAlert]);

  const isEmpty = (item?: string | number) => (item ? item : '--');

  return (
    <QBox maxWidth="640px" mb={2}>
      <QHeading mb={2}>{t('addSupplier.approval.heading')}</QHeading>
      <QFlex align="center" pb={2}>
        <QIcon iconName="Info" color="gray.500" />
        <QText fontSize="sm" color="gray.500" pl={2}>
          {t('addSupplier.approval.infoText')}
        </QText>
      </QFlex>
      <QStack spacing={6}>
        <QBox>
          {showWarningAlert ? (
            <QAlert
              status="warning"
              title={t('addSupplier.approval.warnings.requiredFieldsMissing')}
              description={
                <QBox>
                  <QStack spacing={2}>
                    {Object.entries(errors).map(([name, error], index) => (
                      <WarningMessage
                        key={index}
                        name={name}
                        error={error as string}
                        handleStepChange={handleStepChange}
                      />
                    ))}
                  </QStack>
                </QBox>
              }
            />
          ) : null}
        </QBox>
        <QStack spacing={4}>
          <QHeading size="sm">{t('addSupplier.approval.sections.generalInformation')}</QHeading>
          <ApprovalSimpleItem title={t('addSupplier.approval.fields.supplierName')} description={values.name} />
          <ApprovalSimpleItem
            title={t('addSupplier.approval.fields.supplierType')}
            description={values.supplierType?.label}
          />
          <ApprovalSimpleItem title={t('addSupplier.approval.fields.website')} description={values.website} />
          <ApprovalSimpleItem title={t('addSupplier.approval.fields.intendedUse')} description={values?.intendedUse} />
          <ApprovalSimpleItem title={t('addSupplier.approval.fields.sponsor')} description={values.sponsor?.label} />
        </QStack>

        <QStack spacing={4}>
          <QHeading size="sm">{t('addSupplier.approval.sections.supplierContactInformation')}</QHeading>
          <ApprovalSimpleItem title={t('addSupplier.approval.fields.name')} description={values.contactName} />
          <ApprovalSimpleItem title={t('addSupplier.approval.fields.email')} description={values.contactEmail} />
          <ApprovalSimpleItem title={t('addSupplier.approval.fields.phone')} description={values.contactPhone} />
          <ApprovalSimpleItem title={t('addSupplier.approval.fields.address')} description={values.contactAddress} />
          <ApprovalSimpleItem title={t('addSupplier.approval.fields.notes')} description={values.contactNotes} />
        </QStack>

        <QDivider />
        <QBox maxWidth="800px">
          <QHeading size="sm" pb={4}>
            {t('addSupplier.approval.sections.riskLevel')}
          </QHeading>
          {values?.riskType.title ? (
            <>
              <QTag>{values.riskType?.title}</QTag>
              <QText fontSize="sm" color="gray.500" pt={2}>
                {isEmpty(values.riskType?.description)}
              </QText>
            </>
          ) : (
            <QText fontSize="sm" color="gray.500">
              --
            </QText>
          )}
        </QBox>
        <QDivider />
        <QStack spacing={4}>
          <QHeading size="sm">{t('addSupplier.approval.sections.supportingDocuments')}</QHeading>
          <QBox display="flex" flexDirection="column" maxWidth="800px">
            {formDocuments
              .filter((doc) => doc.name !== ADDITIONAL_DOCUMENTS)
              .map((document: Document, index: number) => (
                <QStack key={index} direction="column" spacing={4}>
                  <QHeading size="xs">{document?.name}</QHeading>
                  {document.files?.length ? (
                    document.files?.map((file: File, index: number) => (
                      <QBox pb={4} key={index}>
                        <ViewFileCard key={index} file={file} />
                      </QBox>
                    ))
                  ) : (
                    <QText fontSize="sm" color="gray.500" pb={4}>
                      {t('addSupplier.approval.messages.noMandatoryDocuments')}
                    </QText>
                  )}
                </QStack>
              ))}
          </QBox>
        </QStack>
        <QStack spacing={4}>
          <QHeading size="sm">{t('addSupplier.approval.sections.additionalDocuments')}</QHeading>
          <QBox display="flex" flexDirection="column" maxWidth="800px">
            {formDocuments.find((doc) => doc.name === ADDITIONAL_DOCUMENTS)?.files?.length ? (
              formDocuments
                .filter((doc) => doc.name === ADDITIONAL_DOCUMENTS)
                .map((document: Document, index: number) => (
                  <QStack key={index} direction="column" spacing={4}>
                    {document.files?.map((file: File, index: number) => (
                      <QBox key={index}>
                        <ViewFileCard key={index} file={file} />
                      </QBox>
                    ))}
                  </QStack>
                ))
            ) : (
              <QText fontSize="sm" color="gray.500">
                {t('addSupplier.approval.messages.noAdditionalDocuments')}
              </QText>
            )}
          </QBox>
        </QStack>
        <QDivider />
        <QStack spacing={2}>
          <QHeading size="sm">{t('addSupplier.approval.sections.requiredAudits')}</QHeading>
          {requiredAudits?.length ? (
            <QText fontSize="sm" color="gray.500">
              {t('addSupplier.approval.messages.requiredAuditsPolicy', {
                type: values?.supplierType?.label,
                level: values?.riskType?.title,
              })}{' '}
              <QLink isExternal href={`/sqm/${routes.policy.root}/${routes.policy.audits}`}>
                {t('addSupplier.approval.supplierPolicyLink')}
              </QLink>
            </QText>
          ) : (
            <QText fontSize="sm" color="gray.500">
              {t('addSupplier.approval.messages.noRequiredAuditsPolicy', {
                type: values?.supplierType?.label,
                level: values?.riskType?.title,
              })}{' '}
              <QLink isExternal href={`/sqm/${routes.policy.root}/${routes.policy.audits}`}>
                {t('addSupplier.approval.supplierPolicyLink')}
              </QLink>
            </QText>
          )}
          {requiredAudits?.length
            ? requiredAudits.map((audit) => (
                <QBox border="1px" borderColor="blackAlpha.200" borderRadius="base" px={3} py={3} key={audit.name}>
                  <QText color="gray.800" fontSize="sm" pb={3}>
                    {audit.name}
                  </QText>
                  <QText color="gray.500" fontSize="xs">
                    {audit.frequency
                      ? t('addSupplier.approval.messages.auditFrequency', {
                          count: audit.frequency as unknown as number,
                        })
                      : t('addSupplier.approval.messages.auditOnce')}
                  </QText>
                </QBox>
              ))
            : ''}
        </QStack>

        <QAlert status="warning" description={t('addSupplier.approval.messages.auditReviewWarning')} />
        <QDivider />

        <QStack spacing={4}>
          <QHeading size="sm">{t('addSupplier.approval.sections.approval')}</QHeading>
          <QFlex align="center" mb={6}>
            <QIcon iconName="Info" color="gray.500" />
            <QText fontSize="sm" color="gray.500" pl={2}>
              {t('addSupplier.approval.messages.approvalInfo')}
            </QText>
          </QFlex>
          <QFormControl
            data-cy="approvers-select"
            label={t('addSupplier.approval.fields.approvers')}
            isInvalid={isFieldInvalid('approvers')}
            error={errors.approvers as string}
            helper={t('addSupplier.generalInfo.fields.sponsor.helper')}
          >
            <QMultiSelect
              name="approvers"
              onChange={(e) => setFieldValue('approvers', e)}
              value={values.approvers.map((user) => user.value)}
              options={usersOptions}
              isLoading={isCompanyUsersLoading}
            />
          </QFormControl>
        </QStack>
      </QStack>
    </QBox>
  );
};
